import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { Task } from '../../../../shared/entities/task/task';
import { ControlTemplate } from '../../../form/entities/controlTemplate';
import { ViewDialogService } from '../../../../../core/components/viewDialog/viewDialogService';
import { CommandTemplate } from '../../../../../core/entities/commandTemplate/commandTemplate';
import { TaskFormHandler } from '../task-form-handler.interface';
import { RentalResourceAssignmentReviewFormHandler } from '../rental-resource-assignment-review-form.handler';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormViewDialogComponent } from './form-view-dialog.component';
import { ResourceAssignment } from '../../../../shared/entities/resourceAssignment/resourceAssignment';

/**
 * Review form view component for rental resource assignment review tasks
 */
@Component({
  selector: 'app-resource-handover-form-view',
  template: `
    <div>
      @if(reviewHandler()?.resourceAssignments()?.length > 0) {
        @for (assignment of reviewHandler()?.resourceAssignments(); track assignment.id) {
          <div class="top flex flex-col px-5 py-[0.313rem] bg-white min-w-[485px] block border-b border-gray first:border-t ng-star-inserted">
          <div class="flex flex-row">
            <div class="grow">
              <div class="flex flex-row">
                <div class="text-base font-medium leading-3r cursor-pointer">
                  {{assignment?.resource?.text}}
                </div>
              </div>
              <div class="text-base text-[#848488] leading-3 ng-star-inserted">
                {{assignment?.lineItem?.article?.name}}
              </div>
            </div>
            @if (!isNotCompleted()) {
            <div class="self-center">
              <nx-default-button
                [style]="'dark'"
                (click)="view(assignment)"
                [disabled]="false"
                [icon]="'icon-search'">
              </nx-default-button>
            </div>
            }
          </div>
        </div>
        }
      }
    </div>
    <div class="flex justify-end mt-2">
      @if(isNotCompleted()) {
      <nx-default-button
        [style]="'accent'"
        [disabled]="disabled()"
        [pTooltip]="disabled() ? 'Im Web nicht verfügbar.' : undefined"
        [label]="'Start'">
      </nx-default-button>
      } @else {
      <nx-default-button
        [style]="'accent'"
        [disabled]="true"
        [label]="'Erledigt'">
      </nx-default-button>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResourceHandoverFormViewComponent {
  readonly task = input.required<Task>();
  readonly template = input<ControlTemplate | null>(null);
  readonly commandTemplate = input<CommandTemplate | null>(null);
  readonly disabled = input(true);
  readonly handler = input.required<TaskFormHandler>();
  
  readonly complete = output<any>();
  
  // Injected services
  private readonly dialogService = inject(ViewDialogService);
  
  dialogRef: DynamicDialogRef | undefined;
  
  // Type-safe accessor for the specific handler
  readonly reviewHandler = computed(() => 
    this.handler() as RentalResourceAssignmentReviewFormHandler
  );

  readonly isNotCompleted = computed(() => this.task()?.state !== 'COMPLETED');

  private readonly TASK_TYPE_TO_PROTOCOL_TYPE: Record<string, string> = {
    'nexcore_rental_resourceAssignmenmt_review': 'protocol.rental.resourceOut',
    'nexcore_rental_resourceAssignmenmt_in': 'protocol.rental.resourceIn',
  };

  view(assignment: ResourceAssignment) {
    const taskTypeCode = this.task()?.taskType?.code;
    const protocolType = this.TASK_TYPE_TO_PROTOCOL_TYPE[taskTypeCode];
    if (!protocolType) {
      console.warn('[ResourceHandoverFormViewComponent] No protocol type mapping for taskType', taskTypeCode);
      return;
    }

    const protocol = assignment.protocols?.find(p => p.type === protocolType);
    if (!protocol) {
      console.warn('[ResourceHandoverFormViewComponent] No matching protocol found for type', protocolType);
      return;
    }

    // Find the correct body index by matching the protocol ID against body[i].key
    const bodyIndex = this.template()?.body?.findIndex(b => b.key === protocol.id);
    if (bodyIndex === undefined || bodyIndex === -1) {
      console.warn('[ResourceHandoverFormViewComponent] No matching body entry found for protocol', protocol.id);
      return;
    }

    this.dialogRef = this.dialogService.open(FormViewDialogComponent, {
      header: this.template()?.settings?.label,
      closeLabel: 'Schließen',
      width: '500px',
      data: {
        template: this.template(),
        index: bodyIndex
      }
    });

    this.dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.complete.emit(result);
      }
    });
  }
}
